import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, Pencil, Trash2, X, Users } from "lucide-react";

const EMPTY = { company: "", site: "", contact: "", mobile: "", email: "", gst_no: "" };

export const AdminCustomers = ({ client, api, perms = [] }) => {
  const canCreate = perms.includes("customers:create");
  const canEdit = perms.includes("customers:edit");
  const canDelete = perms.includes("customers:delete");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`${api}/qv2/customers`);
      setCustomers(data);
    } catch {
      toast.error("Could not load customers");
    } finally {
      setLoading(false);
    }
  }, [client, api]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      if (editing) {
        await client.put(`${api}/qv2/customers/${editing}`, payload);
        toast.success("Customer updated");
      } else {
        await client.post(`${api}/qv2/customers`, payload);
        toast.success("Customer added");
      }
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch {
      toast.error("Could not save customer");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.company}?`)) return;
    try {
      await client.delete(`${api}/qv2/customers/${c.id}`);
      toast.success("Customer removed");
      load();
    } catch {
      toast.error("Could not remove customer");
    }
  };

  const filtered = customers.filter((c) =>
    [c.company, c.site, c.contact, c.mobile, c.email].join(" ").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="customers-panel" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-4">
        <div className="bg-steel border border-white/10 p-6 md:p-7">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke mb-5">{editing ? "Edit Customer" : "Add Customer"}</p>
          {!canCreate && !editing ? (
            <p className="font-body text-sm text-smoke" data-testid="customer-form-readonly">You have view-only access to customers.</p>
          ) : (
          <form data-testid="customer-form" onSubmit={submit} className="flex flex-col gap-4">
            <div>
              <label htmlFor="cust-company" className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Company *</label>
              <input id="cust-company" data-testid="customer-company-input" required minLength={2} value={form.company} onChange={set("company")} className="mt-1 w-full bg-transparent border-0 border-b border-white/25 py-2 font-body text-sm text-white placeholder:text-white/35 focus:border-[#7FB3E8] focus:outline-none transition-colors duration-300" placeholder="Company name" />
            </div>
            <div>
              <label htmlFor="cust-site" className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Site</label>
              <input id="cust-site" data-testid="customer-site-input" value={form.site} onChange={set("site")} className="mt-1 w-full bg-transparent border-0 border-b border-white/25 py-2 font-body text-sm text-white placeholder:text-white/35 focus:border-[#7FB3E8] focus:outline-none transition-colors duration-300" placeholder="Site / address" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="cust-contact" className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Contact</label>
                <input id="cust-contact" data-testid="customer-contact-input" value={form.contact} onChange={set("contact")} className="mt-1 w-full bg-transparent border-0 border-b border-white/25 py-2 font-body text-sm text-white placeholder:text-white/35 focus:border-[#7FB3E8] focus:outline-none transition-colors duration-300" placeholder="Person" />
              </div>
              <div>
                <label htmlFor="cust-mobile" className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Mobile</label>
                <input id="cust-mobile" data-testid="customer-mobile-input" value={form.mobile} onChange={set("mobile")} className="mt-1 w-full bg-transparent border-0 border-b border-white/25 py-2 font-body text-sm text-white placeholder:text-white/35 focus:border-[#7FB3E8] focus:outline-none transition-colors duration-300" placeholder="98..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="cust-email" className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Email</label>
                <input id="cust-email" data-testid="customer-email-input" value={form.email} onChange={set("email")} className="mt-1 w-full bg-transparent border-0 border-b border-white/25 py-2 font-body text-sm text-white placeholder:text-white/35 focus:border-[#7FB3E8] focus:outline-none transition-colors duration-300" placeholder="email" />
              </div>
              <div>
                <label htmlFor="cust-gst" className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">GST No</label>
                <input id="cust-gst" data-testid="customer-gst-input" value={form.gst_no} onChange={set("gst_no")} className="mt-1 w-full bg-transparent border-0 border-b border-white/25 py-2 font-body text-sm text-white placeholder:text-white/35 focus:border-[#7FB3E8] focus:outline-none transition-colors duration-300" placeholder="GSTIN" />
              </div>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button data-testid="customer-submit-button" type="submit" disabled={saving} className="btn-sweep btn-navy font-heading font-semibold uppercase tracking-wider text-base px-6 py-2.5 flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} {editing ? "Save" : "Add"}
              </button>
              {editing && (
                <button type="button" data-testid="customer-cancel-button" onClick={() => { setEditing(null); setForm(EMPTY); }} className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/50 hover:text-white flex items-center gap-1">
                  <X size={13} /> Cancel
                </button>
              )}
            </div>
          </form>
          )}
        </div>
      </div>

      <div className="lg:col-span-8">
        <div className="flex items-center justify-between gap-4 mb-4">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Customers ({filtered.length})</p>
          <input data-testid="customer-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="bg-transparent border-0 border-b border-white/25 py-2 font-body text-sm text-white placeholder:text-white/35 focus:border-[#7FB3E8] focus:outline-none w-48 transition-colors duration-300" />
        </div>
        {filtered.length === 0 && !loading ? (
          <div data-testid="customers-empty" className="bg-steel border border-white/10 p-14 text-center">
            <Users size={40} className="mx-auto text-smoke" strokeWidth={1.5} />
            <p className="mt-4 font-mono text-xs tracking-[0.3em] uppercase text-smoke">No customers yet</p>
          </div>
        ) : (
          <div className="border border-white/10 bg-steel overflow-x-auto">
            <table data-testid="customers-table" className="w-full min-w-[760px]">
              <thead>
                <tr className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke text-left">
                  <th className="py-3 px-4 font-medium">Company</th>
                  <th className="py-3 px-4 font-medium">Site</th>
                  <th className="py-3 px-4 font-medium">Contact</th>
                  <th className="py-3 px-4 font-medium">Mobile</th>
                  <th className="py-3 px-4 font-medium">Email</th>
                  <th className="py-3 px-4 font-medium">GST No</th>
                  <th className="py-3 px-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} data-testid={`customer-row-${c.id.slice(0, 8)}`} className="border-t border-white/10 hover:bg-ink/40 transition-colors duration-300">
                    <td className="py-3.5 px-4 font-heading font-semibold text-base uppercase tracking-wide text-white">{c.company}</td>
                    <td className="py-3.5 px-4 font-mono text-xs text-smoke">{c.site || "—"}</td>
                    <td className="py-3.5 px-4 font-mono text-xs text-white">{c.contact || "—"}</td>
                    <td className="py-3.5 px-4 font-mono text-xs text-smoke whitespace-nowrap">{c.mobile || "—"}</td>
                    <td className="py-3.5 px-4 font-mono text-xs text-smoke">{c.email || "—"}</td>
                    <td className="py-3.5 px-4 font-mono text-xs text-smoke">{c.gst_no || "—"}</td>
                    <td className="py-3.5 px-4 text-right whitespace-nowrap">
                      {canEdit && (
                        <button data-testid={`customer-edit-${c.id.slice(0, 8)}`} onClick={() => { setEditing(c.id); setForm({ company: c.company || "", site: c.site || "", contact: c.contact || "", mobile: c.mobile || "", email: c.email || "", gst_no: c.gst_no || "" }); }} aria-label="Edit" className="p-2 text-white/60 hover:text-white transition-colors duration-300"><Pencil size={15} /></button>
                      )}
                      {canDelete && (
                        <button data-testid={`customer-delete-${c.id.slice(0, 8)}`} onClick={() => remove(c)} aria-label="Delete" className="p-2 text-white/60 hover:text-red-400 transition-colors duration-300"><Trash2 size={15} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
